import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import CalendarView from './components/CalendarView';
import PostCreationForm from './components/PostCreationForm';
import ScheduledPostsList from './components/ScheduledPostsList';
import OptimalTimingRecommendations from './components/OptimalTimingRecommendations';
import BulkUploadModal from './components/BulkUploadModal';
import { scheduledPostService } from '../../services/scheduledPostService';

const ContentScheduler = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [draggedPost, setDraggedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load posts from Supabase
  useEffect(() => {
    loadScheduledPosts();
  }, [filterPlatform]);

  const loadScheduledPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const posts = await scheduledPostService?.getAll(null, filterPlatform);
      setScheduledPosts(posts);
    } catch (err) {
      setError(err?.message || 'Failed to load scheduled posts');
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleCreatePost = async (newPost, platformIds) => {
    try {
      await scheduledPostService?.create(newPost, platformIds);
      await loadScheduledPosts();
    } catch (err) {
      setError(err?.message || 'Failed to create post');
      console.error('Error creating post:', err);
    }
  };

  const handleEditPost = async (post) => {
    console.log('Edit post:', post);
    // TODO: Implement edit functionality with modal
  };

  const handleDeletePost = async (postId) => {
    try {
      await scheduledPostService?.delete(postId);
      await loadScheduledPosts();
    } catch (err) {
      setError(err?.message || 'Failed to delete post');
      console.error('Error deleting post:', err);
    }
  };

  const handleDuplicatePost = async (post) => {
    try {
      await scheduledPostService?.duplicate(post?.id);
      await loadScheduledPosts();
    } catch (err) {
      setError(err?.message || 'Failed to duplicate post');
      console.error('Error duplicating post:', err);
    }
  };

  const handlePostDragStart = (post) => {
    setDraggedPost(post);
  };

  const handlePostDrop = async (post, newDate) => {
    try {
      await scheduledPostService?.update(post?.id, { scheduledDate: newDate }, null);
      await loadScheduledPosts();
    } catch (err) {
      setError(err?.message || 'Failed to update post date');
      console.error('Error updating post:', err);
    }
  };

  const handleBulkUpload = async (posts) => {
    try {
      for (const post of posts) {
        await scheduledPostService?.create(post, post?.platformIds || []);
      }
      await loadScheduledPosts();
    } catch (err) {
      setError(err?.message || 'Failed to upload posts');
      console.error('Error bulk uploading:', err);
    }
  };

  const viewModeOptions = [
  { value: 'calendar', label: 'Calendar View' },
  { value: 'list', label: 'List View' }];


  const platformOptions = [
  { value: 'all', label: 'All Platforms' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' }];


  const filteredPosts = filterPlatform === 'all' ?
  scheduledPosts :
  scheduledPosts?.filter((post) => post?.platforms?.includes(filterPlatform));

  const monthYear = currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const stats = [
  {
    label: 'Total Scheduled',
    value: scheduledPosts?.length || 0,
    icon: 'Calendar',
    color: 'text-primary',
    bgColor: 'bg-primary/10'
  },
  {
    label: 'This Week',
    value: scheduledPosts?.filter((p) => {
      const weekStart = new Date();
      weekStart?.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd?.setDate(weekEnd?.getDate() + 7);
      return p?.scheduledDate >= weekStart && p?.scheduledDate <= weekEnd;
    })?.length || 0,
    icon: 'Clock',
    color: 'text-accent',
    bgColor: 'bg-accent/10'
  },
  {
    label: 'Recurring Posts',
    value: scheduledPosts?.filter((p) => p?.recurring)?.length || 0,
    icon: 'Repeat',
    color: 'text-success',
    bgColor: 'bg-success/10'
  },
  {
    label: 'Platforms Connected',
    value: 4,
    icon: 'Share2',
    color: 'text-warning',
    bgColor: 'bg-warning/10'
  }];


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading scheduled posts...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <main className="main-content">
        <div className="max-w-[1600px] mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Content Scheduler</h1>
              <p className="text-muted-foreground">
                Plan, create, and automate your social media posts across multiple platforms
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/analytics-dashboard')}
                iconName="BarChart3"
                iconPosition="left">

                View Analytics
              </Button>
              <Button
                variant="default"
                onClick={() => setIsBulkUploadOpen(true)}
                iconName="Upload"
                iconPosition="left">

                Bulk Upload
              </Button>
            </div>
          </div>

          {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
              <p className="text-red-600">{error}</p>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-600 hover:text-red-800">

                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats?.map((stat, index) =>
            <div key={index} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{stat?.label}</p>
                    <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${stat?.bgColor} flex items-center justify-center`}>
                    <Icon name={stat?.icon} size={24} className={stat?.color} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreviousMonth}
                      iconName="ChevronLeft" />

                    <h2 className="text-lg font-semibold text-foreground min-w-[200px] text-center">
                      {monthYear}
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextMonth}
                      iconName="ChevronRight" />

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToday}>

                      Today
                    </Button>
                  </div>

                  <div className="flex items-center gap-3">
                    <Select
                      options={platformOptions}
                      value={filterPlatform}
                      onChange={setFilterPlatform}
                      placeholder="Filter by platform"
                      className="w-48" />

                    <Select
                      options={viewModeOptions}
                      value={viewMode}
                      onChange={setViewMode}
                      className="w-40" />

                  </div>
                </div>

                {viewMode === 'calendar' ?
                <CalendarView
                  currentDate={currentDate}
                  scheduledPosts={filteredPosts}
                  onDateSelect={handleDateSelect}
                  onPostDragStart={handlePostDragStart}
                  onPostDrop={handlePostDrop}
                  selectedDate={selectedDate} /> :


                <ScheduledPostsList
                  posts={filteredPosts}
                  onEditPost={handleEditPost}
                  onDeletePost={handleDeletePost}
                  onDuplicatePost={handleDuplicatePost} />

                }
              </div>

              <OptimalTimingRecommendations platform={filterPlatform !== 'all' ? filterPlatform : null} />
            </div>

            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Plus" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Create New Post</h2>
                    <p className="text-sm text-muted-foreground">
                      Schedule for {selectedDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>

                <PostCreationForm
                  onCreatePost={handleCreatePost}
                  selectedDate={selectedDate}
                  onCancel={() => {}}
                  onSchedule={handleCreatePost} />

              </div>
            </div>
          </div>
        </div>
      </main>
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onBulkUpload={handleBulkUpload} />

    </div>);

};

export default ContentScheduler;